import React, { useEffect, useState, useMemo, useCallback, memo } from "react"
import * as url from "../../helpers/url_helper"
import {
    Alert,
    Card,
    CardBody,
    Col,
    Row,
} from "reactstrap"
import { useDispatch, useSelector } from "react-redux"
import {
    getMerchantTags,
    manageMerchantTag,
    clearMerchantTags,
    getTagDefinitions,
    clearTagDefinitions,
} from "store/merchants/actions"
import ConfirmButton from "components/Common/ConfirmButton"
import CButton from "components/Common/CButton"
import SimpleTableContainer from "components/Common/SimpleTableContainer"
import AccessControlWrapper from "components/Common/PermissionButton"
// Memoized tag checkbox - only re-renders when its own checked value changes
const TagCheckbox = memo(({ tag, checked, onToggle, getTagIcon }) => (
    <div className="form-check form-check-primary">
        <input
            type="checkbox"
            className="form-check-input"
            id={`tag_${tag.tagID}`}
            checked={checked}
            onChange={() => {}}
            onClick={() => onToggle(tag.tagID)}
        />
        <label className="form-check-label" htmlFor={`tag_${tag.tagID}`}>
            <div className="d-flex">
                {getTagIcon(tag)}
                <div>
                    <div className="fw-medium">{tag.tag}</div>
                    {tag.description && (
                        <small className="text-muted">{tag.description}</small>
                    )}
                </div>
            </div>
        </label>
    </div>
))
TagCheckbox.displayName = "TagCheckbox"

export default function MerchantTagsForm({
    page,
    match,
    onTabCompletion,
    activeTab,
    tabId,
    createdMerchantID,
}) {
    const [showEditMode, setShowEditMode] = useState(page === "add")
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [selectedTagIDs, setSelectedTagIDs] = useState([])
    const [previousTagsLength, setPreviousTagsLength] = useState(0)

    const dispatch = useDispatch()
    const detailID = match?.params?.id

    // Use createdMerchantID in add mode, detailID in detail mode
    const merchantID = page === "add" ? createdMerchantID : detailID

    const {
        merchantTags,
        tagError,
        isLoadingTags,
        tagDefinitions,
        isLoadingDefinitions,
        definitionError,
        tagManagementSuccess,
    } = useSelector(state => ({
        merchantTags: state.merchants.merchantTags || [],
        tagError: state.merchants.tagError,
        isLoadingTags: state.merchants.isLoadingTags || false,
        tagDefinitions: state.merchants.tagDefinitions || [],
        isLoadingDefinitions: state.merchants.isLoadingDefinitions || false,
        definitionError: state.merchants.definitionError,
        tagManagementSuccess: state.merchants.tagManagementSuccess,
    }))

    // Helper function to check if error should be displayed
    const shouldDisplayError = error => {
        if (!error) return false

        const errorMessage = error.message || error.description || ""
        const errorCode = error.code || error.status || ""

        const noDataPatterns = ["no content available"]

        return !noDataPatterns.some(
            pattern =>
                errorMessage.toLowerCase().includes(pattern) ||
                errorCode.toString().includes("404")
        )
    }

    // Fetch merchant tags only when tab is active
    useEffect(() => {
        const isTabActive = activeTab === tabId

        if (merchantID && isTabActive) {
            dispatch(
                getMerchantTags({
                    headers: {
                        "input-parameters": { merchantID },
                    },
                })
            )
        }
    }, [dispatch, merchantID, page, activeTab, tabId])

    // Fetch tag definitions when in edit mode or add mode
    useEffect(() => {
        if (showEditMode || page === "add") {
            dispatch(
                getTagDefinitions({
                    headers: {
                        "input-parameters": {},
                    },
                })
            )
        }
    }, [dispatch, showEditMode, page])

    // Initialize selected tags when merchant tags are loaded (for edit mode)
    useEffect(() => {
        if (merchantTags.length > 0 && showEditMode && page === "detail") {
            const tagIDs = merchantTags.map(tag => String(tag.merchantTag.tagID))
            setSelectedTagIDs(tagIDs)
        }
    }, [merchantTags, showEditMode, page])

    // Clear data on unmount
    useEffect(() => {
        return () => {
            dispatch(clearMerchantTags())
            dispatch(clearTagDefinitions())
        }
    }, [dispatch])

    // Monitor for successful tag management
    useEffect(() => {
        if (
            tagManagementSuccess &&
            isSubmitting &&
            !tagError
        ) {
            setIsSubmitting(false)
            setShowEditMode(false)

            // Complete tab for add mode
            if (page === "add" && onTabCompletion) {
                onTabCompletion("5") // Tags tab completion
            }
        } else if (tagError && isSubmitting) {
            setIsSubmitting(false)
        }
    }, [
        tagManagementSuccess,
        isSubmitting,
        tagError,
        page,
        onTabCompletion,
    ])

    // Track tags length changes
    useEffect(() => {
        if (merchantTags.length > previousTagsLength) {
            setPreviousTagsLength(merchantTags.length)
        }
    }, [merchantTags.length, previousTagsLength])

    // Handle tag checkbox toggle
    const handleTagToggle = useCallback((tagID) => {
        const id = String(tagID)
        setSelectedTagIDs(prev =>
            prev.includes(id)
                ? prev.filter(x => x !== id)
                : [...prev, id]
        )
    }, [])

    // Handle save tags
    const handleSaveTags = async () => {
        if (!merchantID || selectedTagIDs.length === 0) return

        setIsSubmitting(true)

        const payload = {
            actionType: "create",
            merchantID: parseInt(merchantID),
            tagIDs: selectedTagIDs,
        }

        try {
            dispatch(manageMerchantTag(payload, {}, null))
        } catch (error) {
            setIsSubmitting(false)
            console.error("Error saving tags:", error)
        }
    }

    // Handle edit mode toggle
    const handleEditMode = () => {
        setShowEditMode(true)
        const tagIDs = merchantTags.map(tag => String(tag.merchantTag.tagID))
        setSelectedTagIDs(tagIDs)
    }

    // Handle cancel edit
    const handleCancelEdit = () => {
        setShowEditMode(false)
        setSelectedTagIDs([])
    }

    // Group tag definitions by category
    const groupedTagDefinitions = useMemo(() => {
        const groups = {}
        tagDefinitions.forEach(tag => {
            const categoryName = tag.tagCategory?.category || "Uncategorized"
            if (!groups[categoryName]) {
                groups[categoryName] = []
            }
            groups[categoryName].push(tag)
        })

        return groups
    }, [tagDefinitions])

    // Get tag icon
    const getTagIcon = tag => {
        if (tag.icon) {
            return (
                <img
                    src={tag.icon}
                    alt={tag.tag}
                    style={{ width: "20px", height: "20px", marginRight: "8px" }}
                />
            )
        }
        return <i className="bx bx-tag me-2"></i>
    }

    // Render tag selection form
    const renderTagSelectionForm = () => (
        <Card className="mb-4">
            <CardBody>
                <h6 className="mb-3">Select Tags</h6>
                {Object.keys(groupedTagDefinitions).map(categoryName => (
                    <div key={categoryName} className="mb-4">
                        <h6 className="text-primary mb-3">{categoryName}</h6>
                        <Row>
                            {groupedTagDefinitions[categoryName].map(tag => (
                                <Col md="6" lg="4" key={tag.tagID} className="mb-2">
                                    <TagCheckbox
                                        tag={tag}
                                        checked={selectedTagIDs.includes(String(tag.tagID))}
                                        onToggle={handleTagToggle}
                                        getTagIcon={getTagIcon}
                                    />
                                </Col>
                            ))}
                        </Row>
                    </div>
                ))}

                {selectedTagIDs.length === 0 && (
                    <div className="text-danger mb-3">Please select at least one tag</div>
                )}

                {/* Action buttons */}
                <div className="text-end">
                    {page === "detail" && (
                        <CButton.Secondary
                            text="Cancel"
                            className="me-2"
                            onClick={handleCancelEdit}
                            disabled={isSubmitting}
                        />
                    )}
                    <ConfirmButton
                        contextUri={url.MANAGE_MERCHANT_TAG}
                        actionType="post"
                        validateBeforeConfirm={() => selectedTagIDs.length > 0}
                        onConfirm={() => handleSaveTags()}
                        message="Are you sure you want to save these tags?"
                        disabled={isSubmitting || selectedTagIDs.length === 0}
                        renderButton={
                            <CButton.Primary
                                text="Save Tags"
                                loading={isSubmitting}
                                disabled={isSubmitting || selectedTagIDs.length === 0}
                            />
                        }
                    />
                </div>
            </CardBody>
        </Card>
    )

    return (
        <>
            {/* Error Display */}
            {tagError && shouldDisplayError(tagError) && (
                <Alert color="danger" className="mb-3">
                    {tagError.message || "An error occurred"}
                </Alert>
            )}

            {definitionError && shouldDisplayError(definitionError) && (
                <Alert color="warning" className="mb-3">
                    Unable to load tag definitions: {definitionError.message || "Please try again"}
                </Alert>
            )}

            {/* Tag Selection Form - Show in add mode or edit mode */}
            {showEditMode && renderTagSelectionForm()}

            {/* Tags Display - Show when not editing */}
            {!showEditMode && (
                <SimpleTableContainer
                    title="Tags"
                    data={merchantTags
                        .map((tag, index) => ({
                            ...tag,
                            id: index,
                        }))}
                    columns={[
                        {
                            key: "icon",
                            label: "Icon",
                            width: "60px",
                            align: "center",
                            render: tag => getTagIcon(tag.merchantTag),
                        },
                        {
                            key: "tag",
                            label: "Tag",
                            render: tag => tag.merchantTag
                                .tag || "---",
                        },
                        {
                            key: "description",
                            label: "Description",
                            render: tag => tag.merchantTag
                                .description || "---",
                        },
                        {
                            key: "category",
                            label: "Category",
                            render: tag => tag.merchantTag
                                .tagCategory?.category || "---",
                        },
                    ]}
                    loading={isLoadingTags}
                    emptyMessage="No tags assigned yet"
                    emptyIcon="bx bx-tag"
                    showActions={false}
                    headerActions={
                        merchantTags.length === 0 ? (
                            <AccessControlWrapper
                                contextUri={url.MANAGE_MERCHANT_TAG}
                                actionType="post"
                            >
                                <CButton.Success
                                    icon="bx bx-plus"
                                    text="Add Tags"
                                    onClick={handleEditMode}
                                    disabled={isLoadingDefinitions}
                                />
                            </AccessControlWrapper>
                        ) : (
                            <AccessControlWrapper
                                contextUri={url.MANAGE_MERCHANT_TAG}
                                actionType="post"
                            >
                                <CButton.Primary
                                    text="Edit Tags"
                                    onClick={handleEditMode}
                                    disabled={isLoadingDefinitions}
                                />
                            </AccessControlWrapper>
                        )
                    }
                />
            )}

            {/* Loading state for definitions */}
            {isLoadingDefinitions && showEditMode && (
                <div className="text-center p-5">
                    <div className="spinner-border text-primary mb-3" />
                    <p className="text-muted">Loading tag definitions...</p>
                </div>
            )}
        </>
    )
}
